/**
 * WorkflowAutomationsPage.js
 * 
 * Advanced Workflow Automations UI for Automation Engine V2
 * Manages workflows, rules, conditions, dependencies, and scheduling
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Play, Pause, Settings, Clock, Calendar, Zap, AlertTriangle,
  ChevronDown, ChevronRight, Plus, Edit, Trash2, Copy, Eye,
  RefreshCw, CheckCircle, XCircle, Filter, Search, MoreVertical,
  GitBranch, Layers, Send, Bell, Mail, MessageSquare, ArrowRight,
  Timer, Target, Shield, Activity, History, TestTube
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../components/ui/tabs';
import { Switch } from '../../../components/ui/switch';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { API } from '../../../lib/api';

// Event type labels
const EVENT_LABELS = {
  booking_created: 'Prenotazione Creata',
  booking_confirmed: 'Prenotazione Confermata',
  arrival: 'Arrivo',
  departure: 'Partenza',
  online_checkin_completed: 'Check-in Online Completato',
  guest_checked_in: 'Ospite Arrivato',
  guest_checked_out: 'Ospite Partito',
  review_eligible: 'Recensione Disponibile',
  payment_received: 'Pagamento Ricevuto',
  guest_birthday: 'Compleanno Ospite'
};

// Execution mode labels
const EXECUTION_MODE_LABELS = {
  immediate: 'Immediato',
  relative: 'Relativo',
  fixed: 'Orario Fisso'
};

// Channel icons
const CHANNEL_ICONS = {
  whatsapp: MessageSquare,
  push: Bell,
  email: Mail,
  sms: Send
};

// Missed execution policy labels
const MISSED_POLICY_LABELS = {
  send_immediately: 'Invia Subito',
  skip: 'Salta',
  reschedule_next_valid_window: 'Riprogramma',
  require_manual_review: 'Revisione Manuale'
};

export default function WorkflowAutomationsPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [engineStatus, setEngineStatus] = useState(null);
  const [environmentStatus, setEnvironmentStatus] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showRuleEditor, setShowRuleEditor] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testBookingId, setTestBookingId] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEvent, setFilterEvent] = useState('all');

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [workflowsRes, rulesRes, templatesRes, statusRes, envRes] = await Promise.all([
        axios.get(`${API}/admin/automation/engine/workflows`, authHeaders).catch(() => ({ data: { workflows: [] } })),
        axios.get(`${API}/admin/automation/engine/rules`, authHeaders).catch(() => ({ data: { rules: [] } })),
        axios.get(`${API}/admin/templates`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/automation/engine/status`, authHeaders).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/environment/status`, authHeaders).catch(() => ({ data: {} }))
      ]);

      setWorkflows(workflowsRes.data?.workflows || []);
      setRules(rulesRes.data?.rules || rulesRes.data || []);
      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      setEngineStatus(statusRes.data);
      setEnvironmentStatus(envRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle rule status
  const toggleRuleStatus = async (rule) => {
    try {
      const newStatus = rule.status === 'active' ? 'paused' : 'active';
      await axios.put(`${API}/admin/automation/engine/rules/${rule.id}`, {
        status: newStatus
      }, authHeaders);
      toast.success(`Regola ${newStatus === 'active' ? 'attivata' : 'sospesa'}`);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // Test rule
  const testRule = async (ruleId) => {
    if (!testBookingId) {
      toast.error('Inserisci un ID prenotazione');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/automation/engine/rules/${ruleId}/test`, {
        booking_id: testBookingId
      }, authHeaders);
      setTestResult(response.data);
      toast.success('Test completato');
    } catch (error) {
      toast.error('Errore nel test');
    }
  };

  // Filter rules
  const filteredRules = rules.filter(rule => {
    const matchesSearch = !searchTerm || 
      rule.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.trigger_event?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEvent = filterEvent === 'all' || rule.trigger_event === filterEvent;
    return matchesSearch && matchesEvent;
  });

  // Get template name
  const getTemplateName = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    return template?.name || templateId || 'N/A';
  };

  // Render channel badge
  const renderChannelBadge = (channels) => {
    const primary = channels?.primary || 'whatsapp';
    const Icon = CHANNEL_ICONS[primary] || MessageSquare;
    return (
      <Badge variant="outline" className="gap-1">
        <Icon className="h-3 w-3" />
        {primary.toUpperCase()}
      </Badge>
    );
  };

  // Render timing info
  const renderTimingInfo = (timing) => {
    if (!timing) return 'N/A';
    
    const mode = timing.execution_mode || 'immediate';
    
    if (mode === 'immediate') {
      const delay = timing.delay;
      if (delay && (delay.days || delay.hours || delay.minutes)) {
        const parts = [];
        if (delay.days) parts.push(`${delay.days}g`);
        if (delay.hours) parts.push(`${delay.hours}h`);
        if (delay.minutes) parts.push(`${delay.minutes}m`);
        return `Immediato +${parts.join(' ')}`;
      }
      return 'Immediato';
    }
    
    if (mode === 'relative') {
      const offset = timing.offset_days || 0;
      const time = timing.fixed_time || '09:00';
      const ref = timing.reference_event === 'departure' ? 'partenza' : 'arrivo';
      
      if (offset === 0) return `Giorno ${ref} alle ${time}`;
      if (offset < 0) return `${Math.abs(offset)}g prima ${ref} alle ${time}`;
      return `${offset}g dopo ${ref} alle ${time}`;
    }
    
    return EXECUTION_MODE_LABELS[mode] || mode;
  };

  // Render conditions summary
  const renderConditionsSummary = (conditions) => {
    if (!conditions) return null;
    
    const parts = [];
    if (conditions.require_checkin_completed) parts.push('Check-in completato');
    if (conditions.require_entry_code_available) parts.push('Codice accesso');
    if (conditions.source_mode === 'exclude' && conditions.sources?.length) {
      parts.push(`Escludi: ${conditions.sources.join(', ')}`);
    }
    if (conditions.min_nights) parts.push(`Min ${conditions.min_nights} notti`);
    
    if (parts.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {parts.map((p, i) => (
          <Badge key={i} variant="secondary" className="text-xs">
            {p}
          </Badge>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Workflow Automations</h1>
          <p className="text-gray-500">Automation Engine V2</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
          <Button onClick={() => setShowRuleEditor(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuova Regola
          </Button>
        </div>
      </div>

      {/* Environment Warning Banner */}
      {environmentStatus && (
        <Card className={`border-2 ${environmentStatus.environment?.is_production ? 'border-red-500 bg-red-50' : 'border-green-500 bg-green-50'}`}>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className={`h-5 w-5 ${environmentStatus.environment?.is_production ? 'text-red-600' : 'text-green-600'}`} />
                <div>
                  <p className="font-medium">
                    {environmentStatus.environment?.safety_message}
                  </p>
                  <p className="text-sm text-gray-600">
                    Engine Mode: <span className="font-mono">{engineStatus?.engine_mode || 'legacy'}</span>
                    {engineStatus?.engine_mode === 'v2_shadow' && (
                      <span className="ml-2 text-amber-600">⚠️ V2 Shadow — nessun invio reale V2</span>
                    )}
                  </p>
                </div>
              </div>
              <Badge variant={environmentStatus.environment?.is_production ? 'destructive' : 'default'}>
                {environmentStatus.environment?.APP_ENV?.toUpperCase()}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Layers className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{workflows.length}</p>
                <p className="text-sm text-gray-500">Workflows</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Zap className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{rules.filter(r => r.status === 'active').length}</p>
                <p className="text-sm text-gray-500">Regole Attive</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Pause className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{rules.filter(r => r.status === 'paused').length}</p>
                <p className="text-sm text-gray-500">Regole Sospese</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{engineStatus?.scheduled_executions || 0}</p>
                <p className="text-sm text-gray-500">In Coda</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="rules" className="space-y-4">
        <TabsList>
          <TabsTrigger value="rules" className="gap-2">
            <Zap className="h-4 w-4" />
            Regole V2
          </TabsTrigger>
          <TabsTrigger value="workflows" className="gap-2">
            <Layers className="h-4 w-4" />
            Workflows
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            Storico
          </TabsTrigger>
        </TabsList>

        {/* Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Cerca regole..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterEvent} onValueChange={setFilterEvent}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtra per evento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli eventi</SelectItem>
                {Object.entries(EVENT_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rules List */}
          <div className="space-y-3">
            {filteredRules.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-gray-500">
                  <Zap className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>Nessuna regola V2 trovata</p>
                  <p className="text-sm">Crea una nuova regola o migra quelle esistenti</p>
                </CardContent>
              </Card>
            ) : (
              filteredRules.map((rule) => (
                <Card 
                  key={rule.id}
                  className={`transition-all hover:shadow-md ${rule.status === 'paused' ? 'opacity-60' : ''}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Switch
                            checked={rule.status === 'active'}
                            onCheckedChange={() => toggleRuleStatus(rule)}
                          />
                          <h3 className="font-semibold text-lg">{rule.name}</h3>
                          {renderChannelBadge(rule.channels)}
                          <Badge variant={rule.status === 'active' ? 'default' : 'secondary'}>
                            {rule.status === 'active' ? 'Attiva' : 'Sospesa'}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Trigger</p>
                            <p className="font-medium">{EVENT_LABELS[rule.trigger_event] || rule.trigger_event}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Timing</p>
                            <p className="font-medium">{renderTimingInfo(rule.timing)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Template</p>
                            <p className="font-medium">{getTemplateName(rule.template_id)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Policy Mancato</p>
                            <p className="font-medium">
                              {MISSED_POLICY_LABELS[rule.missed_execution_policy] || 'send_immediately'}
                            </p>
                          </div>
                        </div>
                        
                        {renderConditionsSummary(rule.conditions)}
                        
                        {rule.dependencies?.length > 0 && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                            <GitBranch className="h-4 w-4" />
                            <span>{rule.dependencies.length} dipendenze</span>
                          </div>
                        )}
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedRule(rule);
                            setShowTestDialog(true);
                          }}>
                            <TestTube className="h-4 w-4 mr-2" />
                            Test Regola
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setSelectedRule(rule);
                            setShowRuleEditor(true);
                          }}>
                            <Edit className="h-4 w-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Eye className="h-4 w-4 mr-2" />
                            Storico Esecuzioni
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplica
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Workflows Tab */}
        <TabsContent value="workflows" className="space-y-4">
          {workflows.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <Layers className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>Nessun workflow configurato</p>
                <Button className="mt-4" onClick={() => toast.info('Funzionalità in sviluppo')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crea Workflow
                </Button>
              </CardContent>
            </Card>
          ) : (
            workflows.map((workflow) => (
              <Card key={workflow.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{workflow.name}</CardTitle>
                      <CardDescription>{workflow.description}</CardDescription>
                    </div>
                    <Badge>{workflow.rules_count || 0} regole</Badge>
                  </div>
                </CardHeader>
              </Card>
            ))
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              <History className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>Storico esecuzioni V2</p>
              <p className="text-sm">Le esecuzioni shadow e live saranno mostrate qui</p>
              <Button 
                className="mt-4" 
                variant="outline"
                onClick={() => navigate('/admin/automation/logs')}
              >
                Vai ai Log Completi
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Test Rule Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Test Regola: {selectedRule?.name}</DialogTitle>
            <DialogDescription>
              Inserisci un ID prenotazione per testare questa regola
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>ID Prenotazione</Label>
              <Input
                value={testBookingId}
                onChange={(e) => setTestBookingId(e.target.value)}
                placeholder="es: BOOKING-123"
              />
            </div>
            
            {testResult && (
              <div className="p-4 rounded-lg bg-gray-50 space-y-2">
                <div className="flex items-center gap-2">
                  {testResult.would_send ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <span className="font-medium">
                    {testResult.would_send ? 'Il messaggio verrebbe inviato' : 'Il messaggio NON verrebbe inviato'}
                  </span>
                </div>
                
                {testResult.skip_reason && (
                  <p className="text-sm text-red-600">Motivo: {testResult.skip_reason}</p>
                )}
                
                {testResult.scheduled_at && (
                  <p className="text-sm">Schedulato: {new Date(testResult.scheduled_at).toLocaleString('it-IT')}</p>
                )}
                
                <div className="text-sm space-y-1">
                  <p>Condizioni: {testResult.conditions_met ? '✅' : '❌'}</p>
                  <p>Dipendenze: {testResult.dependencies_met ? '✅' : '❌'}</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              Chiudi
            </Button>
            <Button onClick={() => selectedRule && testRule(selectedRule.id)}>
              <TestTube className="h-4 w-4 mr-2" />
              Esegui Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule Editor Dialog */}
      <Dialog open={showRuleEditor} onOpenChange={setShowRuleEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedRule ? `Modifica: ${selectedRule.name}` : 'Nuova Regola V2'}
            </DialogTitle>
          </DialogHeader>
          
          <RuleEditorForm
            rule={selectedRule}
            templates={templates}
            onSave={async (data) => {
              try {
                if (selectedRule) {
                  await axios.put(`${API}/admin/automation/engine/rules/${selectedRule.id}`, data, authHeaders);
                  toast.success('Regola aggiornata');
                } else {
                  await axios.post(`${API}/admin/automation/engine/rules`, data, authHeaders);
                  toast.success('Regola creata');
                }
                setShowRuleEditor(false);
                setSelectedRule(null);
                fetchData();
              } catch (error) {
                toast.error('Errore nel salvataggio');
              }
            }}
            onCancel={() => {
              setShowRuleEditor(false);
              setSelectedRule(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Rule Editor Form Component
function RuleEditorForm({ rule, templates, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    trigger_event: rule?.trigger_event || 'booking_created',
    template_id: rule?.template_id || '',
    status: rule?.status || 'active',
    timing: rule?.timing || {
      execution_mode: 'immediate',
      delay: { days: 0, hours: 0, minutes: 0 },
      reference_event: 'arrival',
      offset_days: 0,
      fixed_time: '09:00'
    },
    channels: rule?.channels || {
      primary: 'whatsapp',
      fallback: []
    },
    conditions: rule?.conditions || {
      exclude_cancelled: true,
      exclude_no_contact: true
    },
    missed_execution_policy: rule?.missed_execution_policy || 'send_immediately',
    retry: rule?.retry || {
      enabled: true,
      max_attempts: 3,
      delay_minutes: 30
    }
  });

  const updateFormData = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Nome Regola *</Label>
          <Input
            value={formData.name}
            onChange={(e) => updateFormData('name', e.target.value)}
            placeholder="es: Conferma Prenotazione"
          />
        </div>
        
        <div>
          <Label>Evento Trigger *</Label>
          <Select
            value={formData.trigger_event}
            onValueChange={(v) => updateFormData('trigger_event', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(EVENT_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Template *</Label>
          <Select
            value={formData.template_id}
            onValueChange={(v) => updateFormData('template_id', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleziona template" />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Timing */}
      <div className="space-y-4">
        <h4 className="font-medium flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Timing
        </h4>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Modalità Esecuzione</Label>
            <Select
              value={formData.timing.execution_mode}
              onValueChange={(v) => updateFormData('timing.execution_mode', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Immediato</SelectItem>
                <SelectItem value="relative">Relativo a Data</SelectItem>
                <SelectItem value="fixed">Orario Fisso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {formData.timing.execution_mode === 'relative' && (
            <>
              <div>
                <Label>Riferimento</Label>
                <Select
                  value={formData.timing.reference_event}
                  onValueChange={(v) => updateFormData('timing.reference_event', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="arrival">Data Arrivo</SelectItem>
                    <SelectItem value="departure">Data Partenza</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Offset Giorni</Label>
                <Input
                  type="number"
                  value={formData.timing.offset_days}
                  onChange={(e) => updateFormData('timing.offset_days', parseInt(e.target.value) || 0)}
                />
              </div>
              
              <div>
                <Label>Orario</Label>
                <Input
                  type="time"
                  value={formData.timing.fixed_time}
                  onChange={(e) => updateFormData('timing.fixed_time', e.target.value)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Missed Execution Policy */}
      <div>
        <Label>Policy Esecuzione Mancata</Label>
        <Select
          value={formData.missed_execution_policy}
          onValueChange={(v) => updateFormData('missed_execution_policy', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="send_immediately">Invia Subito</SelectItem>
            <SelectItem value="skip">Salta</SelectItem>
            <SelectItem value="reschedule_next_valid_window">Riprogramma</SelectItem>
            <SelectItem value="require_manual_review">Revisione Manuale</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 mt-1">
          Cosa fare se l&apos;orario programmato è già passato
        </p>
      </div>

      {/* Conditions */}
      <div className="space-y-3">
        <h4 className="font-medium flex items-center gap-2">
          <Target className="h-4 w-4" />
          Condizioni
        </h4>
        
        <div className="flex items-center gap-3">
          <Switch
            checked={formData.conditions.require_checkin_completed}
            onCheckedChange={(v) => updateFormData('conditions.require_checkin_completed', v)}
          />
          <Label>Richiedi Check-in Online Completato</Label>
        </div>
        
        <div className="flex items-center gap-3">
          <Switch
            checked={formData.conditions.require_entry_code_available}
            onCheckedChange={(v) => updateFormData('conditions.require_entry_code_available', v)}
          />
          <Label>Richiedi Codice Accesso Disponibile</Label>
        </div>
        
        <div className="flex items-center gap-3">
          <Switch
            checked={formData.conditions.exclude_cancelled}
            onCheckedChange={(v) => updateFormData('conditions.exclude_cancelled', v)}
          />
          <Label>Escludi Prenotazioni Cancellate</Label>
        </div>
      </div>

      {/* Channel */}
      <div>
        <Label>Canale Primario</Label>
        <Select
          value={formData.channels.primary}
          onValueChange={(v) => updateFormData('channels.primary', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="push">Push Notification</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Retry */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Switch
            checked={formData.retry.enabled}
            onCheckedChange={(v) => updateFormData('retry.enabled', v)}
          />
          <Label>Abilita Retry Automatico</Label>
        </div>
        
        {formData.retry.enabled && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Max Tentativi</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={formData.retry.max_attempts}
                onChange={(e) => updateFormData('retry.max_attempts', parseInt(e.target.value) || 3)}
              />
            </div>
            <div>
              <Label>Delay (minuti)</Label>
              <Input
                type="number"
                min={1}
                value={formData.retry.delay_minutes}
                onChange={(e) => updateFormData('retry.delay_minutes', parseInt(e.target.value) || 30)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button onClick={() => onSave(formData)}>
          {rule ? 'Salva Modifiche' : 'Crea Regola'}
        </Button>
      </div>
    </div>
  );
}
